import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { WebPreviewBody } from "@/components/ai-elements/web-preview";
import { imagineStore } from "@/stores/imagine.store";

type WebPreviewFrameProps = {
  src: string;
};

export function AnimatedWebPreviewFrame({ src }: WebPreviewFrameProps) {
  const viewMode = imagineStore.use.previewState.viewMode();
  const isMobile = viewMode === "mobile";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const MOBILE_ASPECT = 9 / 19.5; // width / height

  const computeSize = useCallback(() => {
    if (!isMobile) {
      // For non-mobile, we don't need to calculate size - we'll use 100% width/height
      setSize(null);
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const paddingX =
      parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const paddingY =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const containerWidth = el.clientWidth - paddingX;
    const containerHeight = el.clientHeight - paddingY;

    const mobilePadding = 30;
    const availableWidth = containerWidth - mobilePadding * 2;
    const availableHeight = containerHeight - mobilePadding * 2;

    let targetHeight = availableHeight;
    let targetWidth = targetHeight * MOBILE_ASPECT;
    if (targetWidth > availableWidth) {
      targetWidth = availableWidth;
      targetHeight = targetWidth / MOBILE_ASPECT;
    }
    setSize({
      width: Math.max(0, targetWidth),
      height: Math.max(0, targetHeight),
    });
  }, [isMobile, MOBILE_ASPECT]);

  useLayoutEffect(() => {
    computeSize();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => computeSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [computeSize]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-1 items-center justify-center",
        isMobile && "box-border bg-muted",
      )}
    >
      <div
        className={cn(
          "overflow-hidden",
          !isMobile && "size-full",
          isMobile && "shadow-xs border border-border/30",
        )}
        style={
          isMobile && size
            ? {
                width: size.width,
                height: size.height,
                borderRadius: 16,
              }
            : {
                borderRadius: isMobile ? 16 : 0,
              }
        }
      >
        <WebPreviewBody src={src} className="size-full" onLoad={() => {}} />
      </div>
    </div>
  );
}
